import { useEffect, useRef, useState } from "react";
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  Popover,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  DEFAULT_MODEL_TIER,
  MODEL_TIERS,
  formatProviderLabel,
  getTierLabel,
} from "./providerModelTiers";

const DROPDOWN_OPEN_ANIMATION = {
  "@keyframes dropdownFadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  animation: "dropdownFadeIn 140ms ease-out",
};

const menuPaperSx = {
  mt: 0.75,
  minWidth: 168,
  maxHeight: 280,
  p: 0.5,
  background:
    "linear-gradient(145deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%)",
  bgcolor: "transparent",
  backdropFilter: "blur(28px) saturate(140%)",
  WebkitBackdropFilter: "blur(28px) saturate(140%)",
  border: "none",
  borderRadius: "16px",
  color: "rgba(255,255,255,0.96)",
  boxShadow:
    "inset 0 1px 0 rgba(255,255,255,0.22), 0 16px 40px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.12)",
  overflow: "visible",
  ...DROPDOWN_OPEN_ANIMATION,
  "& .MuiList-root": {
    py: 0.25,
    background: "transparent",
  },
};

const menuItemSx = {
  position: "relative",
  fontSize: 13,
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 500,
  letterSpacing: "0.2px",
  minHeight: 40,
  mx: 0,
  my: 0.2,
  px: 1.25,
  pr: 4,
  py: 1,
  borderRadius: "12px",
  color: "rgba(255,255,255,0.82)",
  transition: "background 0.18s ease, color 0.18s ease",
  "&:hover": {
    background: "rgba(255,255,255,0.14)",
    color: "#fff",
  },
  "&.Mui-selected": {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    fontWeight: 600,
    "&:hover": {
      background: "rgba(255,255,255,0.26)",
    },
  },
  "&.Mui-focusVisible": {
    background: "rgba(255,255,255,0.14)",
  },
  "&.Mui-disabled": {
    opacity: 0.45,
  },
};

const checkmarkSx = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  fontSize: 12,
  fontWeight: 700,
  color: "rgba(255,255,255,0.85)",
};

export default function ProviderTierSelect({
  isHero = false,
  providers = [],
  selectedProviderId = "",
  onProviderChange,
  onProviderHover,
  selectedTier = DEFAULT_MODEL_TIER,
  onTierChange,
  loadingProviders = false,
  loadingModels = false,
  disabled = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [tierMenuAnchor, setTierMenuAnchor] = useState(null);
  const closeTimerRef = useRef(null);
  const tierRowRef = useRef(null);

  const menuOpen = Boolean(menuAnchor);
  const tierMenuOpen = Boolean(tierMenuAnchor);

  const selectedProvider = providers.find(
    (provider) => String(provider.providerId) === String(selectedProviderId),
  );

  const providerLabel = selectedProvider
    ? formatProviderLabel(selectedProvider.providerName)
    : loadingProviders
      ? "Loading…"
      : "Provider";

  const tierLabel = getTierLabel(selectedTier);
  const triggerLabel = `${providerLabel} ${tierLabel}`;

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closeAll = () => {
    clearCloseTimer();
    setTierMenuAnchor(null);
    setMenuAnchor(null);
  };

  useEffect(() => () => clearCloseTimer(), []);

  const openMenu = (event) => {
    if (disabled || loadingProviders || providers.length === 0) return;
    setMenuAnchor(event.currentTarget);
  };

  const openTierMenu = () => {
    clearCloseTimer();
    if (tierRowRef.current) {
    setTierMenuAnchor(tierRowRef.current);
    }
  };

  const scheduleCloseTierMenu = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setTierMenuAnchor(null);
    }, 180);
  };

  const handleProviderSelect = (providerId) => {
    onProviderChange?.(String(providerId));
  };

  const handleTierSelect = (tierId) => {
    onTierChange?.(tierId);
    closeAll();
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={openMenu}
        disabled={disabled || loadingProviders || providers.length === 0}
        aria-haspopup="menu"
        aria-expanded={menuOpen ? "true" : undefined}
        aria-label="Select provider and model tier"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          height: isHero ? 36 : undefined,
          lineHeight: 1.2,
          overflow: "visible",
          boxSizing: "border-box",
          gap: 0.25,
          maxWidth: isHero ? 220 : 200,
          py: isHero ? 0 : "5px",
          pl: isHero ? 0.25 : 0.75,
          pr: 0.5,
          m: 0,
          border: "none",
          borderRadius: "999px",
          background: menuOpen ? "rgba(255,255,255,0.1)" : "transparent",
          color: "rgba(255,255,255,0.92)",
          fontSize: isHero ? 13 : 12.5,
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          letterSpacing: "0.2px",
          cursor:
            disabled || loadingProviders || providers.length === 0
              ? "not-allowed"
              : "pointer",
          opacity: disabled || loadingProviders || providers.length === 0 ? 0.45 : 1,
          transition: "background 0.2s ease",
          "&:hover": {
            background:
              disabled || loadingProviders || providers.length === 0
                ? "transparent"
                : "rgba(255,255,255,0.1)",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {loadingModels && !selectedProvider ? "Loading…" : triggerLabel}
        </Box>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 18,
            color: "rgba(255,255,255,0.65)",
            flexShrink: 0,
            transition: "transform 0.2s ease, color 0.2s ease",
            transform: menuOpen ? "rotate(180deg)" : "none",
          }}
        />
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeAll}
        disableScrollLock
        transitionDuration={0}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{
          dense: true,
          sx: { outline: "none" },
        }}
        PaperProps={{
          sx: menuPaperSx,
          onMouseLeave: scheduleCloseTierMenu,
        }}
      >
        {providers.length === 0 ? (
          <MenuItem disabled sx={menuItemSx}>
            {loadingProviders ? "Loading…" : "No providers"}
          </MenuItem>
        ) : (
          providers.map((provider) => {
            const selected =
              String(provider.providerId) === String(selectedProviderId);
            return (
              <MenuItem
                key={provider.providerId}
                selected={selected}
                onClick={() => handleProviderSelect(provider.providerId)}
                onMouseEnter={() => {
                  scheduleCloseTierMenu();
                  onProviderHover?.(provider.providerId);
                }}
                sx={menuItemSx}
              >
                {formatProviderLabel(provider.providerName)}
                {selected ? (
                  <Box component="span" sx={checkmarkSx}>
                    ✓
                  </Box>
                ) : null}
              </MenuItem>
            );
          })
        )}

        <Divider
          sx={{
            my: 0.75,
            mx: 0.75,
            borderColor: "rgba(255,255,255,0.14)",
          }}
        />

        <MenuItem
          ref={tierRowRef}
          onMouseEnter={openTierMenu}
          onFocus={openTierMenu}
          onClick={openTierMenu}
          selected={tierMenuOpen}
          sx={{
            ...menuItemSx,
            pr: 1.25,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box component="span">{tierLabel}</Box>
          <ChevronRightIcon
            sx={{ fontSize: 18, color: "rgba(255,255,255,0.65)" }}
          />
        </MenuItem>
      </Menu>

      <Popover
        open={tierMenuOpen}
        anchorEl={tierMenuAnchor}
        onClose={() => setTierMenuAnchor(null)}
        disableScrollLock
        disableRestoreFocus
        disableAutoFocus
        disableEnforceFocus
        transitionDuration={0}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          root: {
        sx: { pointerEvents: "none" },
          },
          paper: {
          onMouseEnter: clearCloseTimer,
          onMouseLeave: scheduleCloseTierMenu,
          sx: {
            ...menuPaperSx,
            mt: 0,
            ml: 1,
            minWidth: 132,
            pointerEvents: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.25 }}>
        {MODEL_TIERS.map((tier) => {
          const selected = tier.id === selectedTier;
          return (
            <MenuItem
              key={tier.id}
              selected={selected}
              onClick={() => handleTierSelect(tier.id)}
              sx={menuItemSx}
            >
              {tier.label}
              {selected ? (
                <Box component="span" sx={checkmarkSx}>
                  ✓
                </Box>
              ) : null}
            </MenuItem>
          );
        })}
        </Box>
      </Popover>
    </>
  );
}
